import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Mail, Phone, AlertTriangle, CheckCircle, ExternalLink, Ban, RotateCcw, ChevronDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

interface RegistrationTicket {
  id: string;
  ticket_class_name: string;
  attendee_first_name: string | null;
  attendee_last_name: string | null;
  attendee_email: string | null;
  cost_cents: number;
  cost_currency: string;
  status: string;
}

interface Registration {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  attendee_type: string | null;
  ticket_count: number;
  has_ticket_conflict: boolean;
  conflict_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  eventbrite_order_id: string | null;
  // Exclusion fields
  is_excluded?: boolean;
  excluded_reason?: string | null;
  excluded_at?: string | null;
  excluded_by?: string | null;
}

interface Props {
  registration: Registration | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewComplete?: () => void;
}

const EXCLUSION_REASONS = [
  "Test registration",
  "Duplicate order",
  "Staff/internal",
  "Refunded/cancelled",
  "Data entry error",
];

const RegistrationDetailDialog: React.FC<Props> = ({
  registration,
  open,
  onOpenChange,
  onReviewComplete,
}) => {
  const [tickets, setTickets] = useState<RegistrationTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [excluding, setExcluding] = useState(false);

  useEffect(() => {
    if (open && registration) {
      fetchTickets();
    }
  }, [open, registration?.id]);

  const fetchTickets = async () => {
    if (!registration) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("registration_tickets")
      .select("*")
      .eq("registration_id", registration.id)
      .order("ticket_class_name");

    if (!error && data) {
      setTickets(data as RegistrationTicket[]);
    }
    setLoading(false);
  };

  const markAsReviewed = async () => {
    if (!registration) return;
    setMarking(true);

    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from("event_registrations")
      .update({
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id,
      })
      .eq("id", registration.id);

    if (error) {
      toast.error("Failed to mark as reviewed");
    } else {
      toast.success("Marked as reviewed");
      onOpenChange(false);
      onReviewComplete?.();
    }
    setMarking(false);
  };

  const excludeRegistration = async (reason: string) => {
    if (!registration) return;
    setExcluding(true);

    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from("event_registrations")
      .update({
        is_excluded: true,
        excluded_reason: reason,
        excluded_at: new Date().toISOString(),
        excluded_by: user?.id,
      })
      .eq("id", registration.id);

    if (error) {
      toast.error("Failed to exclude registration");
    } else {
      toast.success("Registration excluded from counts");
      onOpenChange(false);
      onReviewComplete?.();
    }
    setExcluding(false);
  };

  const restoreRegistration = async () => {
    if (!registration) return;
    setExcluding(true);

    const { error } = await supabase
      .from("event_registrations")
      .update({
        is_excluded: false,
        excluded_reason: null,
        excluded_at: null,
        excluded_by: null,
      })
      .eq("id", registration.id);

    if (error) {
      toast.error("Failed to restore registration");
    } else {
      toast.success("Registration restored to counts");
      onOpenChange(false);
      onReviewComplete?.();
    }
    setExcluding(false);
  };

  const formatCurrency = (cents: number, currency: string) => {
    const amount = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  if (!registration) return null;

  const fullName = [registration.first_name, registration.last_name].filter(Boolean).join(" ") || "Unknown";
  const isExcluded = registration.is_excluded;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Registration Details</SheetTitle>
          <SheetDescription>
            View full registration and ticket information
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Exclusion Status Banner */}
          {isExcluded && (
            <div className="p-4 rounded-lg bg-gray-500/10 border border-gray-500/30 space-y-2">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Ban className="h-5 w-5" />
                <span className="font-semibold">Excluded from Counts</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Reason: {registration.excluded_reason || "No reason specified"}
              </p>
              {registration.excluded_at && (
                <p className="text-xs text-muted-foreground">
                  Excluded on {format(parseISO(registration.excluded_at), "MMM d, yyyy")}
                </p>
              )}
            </div>
          )}

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Contact Information
            </h3>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-lg">{fullName}</p>
                <p className="text-sm text-muted-foreground">
                  {registration.attendee_type || "No type specified"}
                </p>
              </div>

              {registration.email && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{registration.email}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`mailto:${registration.email}`}>
                      <Mail className="h-4 w-4 mr-1" />
                      Email
                    </a>
                  </Button>
                </div>
              )}

              {registration.phone && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{registration.phone}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`tel:${registration.phone}`}>
                      <Phone className="h-4 w-4 mr-1" />
                      Call
                    </a>
                  </Button>
                </div>
              )}

              {registration.eventbrite_order_id && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ExternalLink className="h-4 w-4" />
                  Order: {registration.eventbrite_order_id}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Registered: {format(parseISO(registration.created_at), "MMM d, yyyy 'at' h:mm a")}
              </p>
            </div>
          </div>

          {/* Tickets Purchased */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Tickets Purchased ({registration.ticket_count})
            </h3>
            
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : tickets.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket Type</TableHead>
                      <TableHead>Attendee</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id}>
                        <TableCell className="font-medium">
                          {ticket.ticket_class_name}
                          <Badge
                            variant="outline"
                            className="ml-2 text-xs"
                          >
                            {ticket.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ticket.attendee_first_name || ticket.attendee_last_name
                            ? `${ticket.attendee_first_name || ""} ${ticket.attendee_last_name || ""}`.trim()
                            : "—"}
                          {ticket.attendee_email && (
                            <span className="block text-xs text-muted-foreground">
                              {ticket.attendee_email}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(ticket.cost_cents, ticket.cost_currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No individual ticket data available
              </p>
            )}
          </div>

          {/* Conflict Warning */}
          {registration.has_ticket_conflict && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2">
              <div className="flex items-center gap-2 text-amber-500">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-semibold">Potential Issue Detected</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {registration.conflict_reason}
              </p>
              <p className="text-xs text-muted-foreground">
                This may indicate the registrant was confused about ticket types.
                Consider reaching out to clarify their registration.
              </p>
            </div>
          )}

          {/* Actions Section */}
          <div className="pt-4 border-t space-y-3">
            {/* Exclusion/Restore Actions */}
            {isExcluded ? (
              <Button
                onClick={restoreRegistration}
                disabled={excluding}
                variant="outline"
                className="w-full"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {excluding ? "Restoring..." : "Restore to Counts"}
              </Button>
            ) : (
              <div className="flex gap-2">
                {/* Review Status/Action */}
                {registration.reviewed_at ? (
                  <div className="flex-1 flex items-center gap-2 text-emerald-500 p-2 bg-emerald-500/10 rounded-lg">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm">
                      Reviewed {format(parseISO(registration.reviewed_at), "MMM d")}
                    </span>
                  </div>
                ) : registration.has_ticket_conflict ? (
                  <Button
                    onClick={markAsReviewed}
                    disabled={marking}
                    className="flex-1"
                  >
                    {marking ? "Marking..." : "Mark as Reviewed"}
                  </Button>
                ) : null}

                {/* Exclude Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      disabled={excluding}
                      className="gap-2"
                    >
                      <Ban className="h-4 w-4" />
                      Exclude
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {EXCLUSION_REASONS.map((reason) => (
                      <DropdownMenuItem 
                        key={reason}
                        onClick={() => excludeRegistration(reason)}
                      >
                        {reason}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RegistrationDetailDialog;